import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, LayoutGroup, motion } from 'motion/react'
import { Link, useParams } from 'react-router-dom'
import { ApiError, errorMessage } from '@/api/client'
import { useConversation, useConversations, useMeta } from '@/api/queries'
import { chatController } from '@/stores/chatController'
import { chatStore, conversationKey, NEW_CHAT_KEY, selectIsStreaming, useChatStore } from '@/stores/chatStore'
import { useUiStore } from '@/stores/uiStore'
import { ease } from '@/lib/motion'
import { Composer } from '@/components/chat/Composer'
import { EmptyState } from '@/components/chat/EmptyState'
import { MessageList } from '@/components/chat/MessageList'
import { MessageScroller, type MessageScrollerHandle } from '@/components/chat/MessageScroller'
import { TopBar } from '@/components/layout/TopBar'
import { SourcesPanel } from '@/components/sources/SourcesPanel'
import { Button } from '@/components/ui/Button'
import { Notice } from '@/components/ui/Notice'
import { Skeleton } from '@/components/ui/Skeleton'

function MessagesSkeleton() {
  return (
    <div className="mx-auto w-full max-w-reading space-y-8 px-4 pt-8 sm:px-6" aria-label="Loading conversation">
      <Skeleton className="ml-auto h-10 w-1/2 rounded-2xl" />
      <div className="space-y-2.5">
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-3/5" />
      </div>
      <Skeleton className="ml-auto h-10 w-2/5 rounded-2xl" />
      <div className="space-y-2.5">
        <Skeleton className="h-4 w-10/12" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  )
}

export function ChatPage() {
  const params = useParams()
  const conversationId = params.id ?? null
  const key = conversationKey(conversationId)
  const meta = useMeta()
  const detail = useConversation(conversationId)
  const { data: conversations } = useConversations()
  const hasLiveTurn = useChatStore((s) => Boolean(s.byConversation[key]))
  const liveTitleKnown = useChatStore((s) => Boolean(s.byConversation[key] && s.turns[s.byConversation[key]]))
  const isStreaming = useChatStore((s) => selectIsStreaming(s, key))
  const scroller = useRef<MessageScrollerHandle>(null)

  // Identity of the visible conversation for the cross-fade. A new chat that just received its id
  // keeps its identity (no flash mid-answer); opening a different chat gets a new one.
  const [listIdentity, setListIdentity] = useState(conversationId ?? 'new')
  const [identityFor, setIdentityFor] = useState(conversationId)
  if (identityFor !== conversationId) {
    setIdentityFor(conversationId)
    const promoted = identityFor === null && conversationId !== null && hasLiveTurn
    if (!promoted) setListIdentity(conversationId ?? 'new')
  }

  const title = conversationId
    ? (conversations?.find((c) => c.id === conversationId)?.title ?? detail.data?.title ?? 'Conversation')
    : 'New chat'

  useEffect(() => {
    document.title = `${title} · ${meta.assistant_name}`
  }, [title, meta.assistant_name])

  const prevId = useRef(conversationId)
  useEffect(() => {
    const previous = prevId.current
    prevId.current = conversationId
    if (previous === conversationId) return
    // Switching conversations closes the sources panel (a new chat getting its id does not).
    if (previous !== null) useUiStore.getState().closeSources()
    // A new chat was promoted to /c/:id: release its 'new' mapping now that the route changed.
    const state = chatStore.get()
    const newStream = state.byConversation[NEW_CHAT_KEY]
    if (conversationId && newStream && state.byConversation[conversationId] === newStream) {
      chatStore.clearNewChat()
    }
  }, [conversationId])

  const send = useCallback(
    (text: string) => {
      const ok = chatController.send(conversationId, text, useUiStore.getState().department)
      if (ok) scroller.current?.stickToBottom()
      return ok
    },
    [conversationId],
  )
  const stop = useCallback(() => {
    chatController.stop(conversationId)
  }, [conversationId])
  const onEdited = useCallback(() => scroller.current?.stickToBottom(), [])

  const showEmpty = !conversationId && !hasLiveTurn
  const error = detail.error
  const loading = Boolean(conversationId) && detail.isPending && !liveTitleKnown

  let body: ReactNode
  if (error && !detail.data) {
    const status = error instanceof ApiError ? error.status : 0
    body = (
      <div className="mx-auto w-full max-w-reading px-4 pt-12 sm:px-6">
        {status === 404 ? (
          <Notice
            title="Conversation not found"
            action={
              <Link to="/" className="text-sm font-medium text-accent-text hover:underline">
                Start a new chat
              </Link>
            }
          >
            It may have been deleted, or the link is incorrect.
          </Notice>
        ) : status === 403 ? (
          <Notice tone="locked" title="You don&rsquo;t have access">
            This conversation belongs to someone else.
          </Notice>
        ) : (
          <Notice
            tone="danger"
            title="Couldn&rsquo;t load this conversation"
            action={
              <Button variant="outline" size="sm" onClick={() => void detail.refetch()}>
                Try again
              </Button>
            }
          >
            {errorMessage(error)}
          </Notice>
        )}
      </div>
    )
  } else if (loading) {
    body = <MessagesSkeleton />
  } else {
    body = <MessageList conversationId={conversationId} messages={detail.data?.messages} onEdited={onEdited} />
  }

  const composerProps = { conversationId, isStreaming, onSend: send, onStop: stop }

  return (
    <>
      <main id="main" className="relative flex min-w-0 flex-1 flex-col" aria-label="Chat">
        <TopBar title={showEmpty ? '' : title} floating={showEmpty} />
        <LayoutGroup>
          <div className="relative flex min-h-0 flex-1 flex-col">
            <AnimatePresence mode="popLayout">
              {showEmpty ? (
                <motion.div
                  key="welcome"
                  className="absolute inset-0 overflow-y-auto"
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: ease.out }}
                >
                  <EmptyState onPick={send} composer={<Composer {...composerProps} placement="hero" />} />
                </motion.div>
              ) : (
                <motion.div
                  key="conversation"
                  className="flex min-h-0 flex-1 flex-col"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: ease.out }}
                >
                  <MessageScroller ref={scroller} resetKey={conversationId ?? 'new'}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={listIdentity}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, transition: { duration: 0.12 } }}
                        transition={{ duration: 0.35, ease: ease.out }}
                      >
                        {body}
                      </motion.div>
                    </AnimatePresence>
                  </MessageScroller>
                  <Composer {...composerProps} placement="dock" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </LayoutGroup>
      </main>
      <SourcesPanel conversationId={conversationId} />
    </>
  )
}
